import axios from "axios";

import {
  FETCH_TEST_PAGES,
  SUBMIT_TEST_RESULT,
  FETCH_TEST_HISTORY,
  LOADING_TEST,
  FETCH_PAGES,
  FETCH_WORD_LEVEL_PROGRESS,
  LEVEL_UNLOCKED
} from "./type";

/**
 * Fetch random test pages for a book
 * @param {string} bookId - Book ID
 * @param {function} callback - Callback function to execute after fetch
 */
export const fetchTestPages = (bookId, callback) => async (dispatch) => {
  try {
    dispatch({ type: LOADING_TEST, payload: true });

    const { data } = await axios.get(`test/book/${bookId}/random-pages`);

    // Convert pages array to pageMap object for the page reducer
    const pageMap = data.pages.reduce((acc, page) => {
      acc[page._id] = page;
      return acc;
    }, {});

    // Add test pages to pageMap so WordStyle1 component can access them
    dispatch({
      type: FETCH_PAGES,
      payload: { pageMap }
    });

    // Store test-specific data in test reducer
    dispatch({
      type: FETCH_TEST_PAGES,
      payload: {
        pages: data.pages,
        bookLevel: data.bookLevel
      }
    });

    dispatch({ type: LOADING_TEST, payload: false });

    if (callback) {
      callback(data.pages, data.bookLevel);
    }
  } catch (error) {
    console.error("Error fetching test pages:", error);
    dispatch({ type: LOADING_TEST, payload: false });

    if (callback) {
      callback([], null);
    }
  }
};

/**
 * Submit test result
 * @param {object} testData - Complete test result data
 * @param {function} callback - Callback function to execute after submission
 */
export const submitTestResult = (testData, callback) => async (dispatch) => {
  try {
    const { data } = await axios.post("test/result", testData);

    dispatch({
      type: SUBMIT_TEST_RESULT,
      payload: {
        testResult: data.testResult
      }
    });

    // Handle level unlock
    if (data.levelUnlocked) {
      dispatch({
        type: LEVEL_UNLOCKED,
        payload: {
          booksetId: testData._bookset,
          newMaxLevel: data.newMaxLevel
        }
      });
    }

    if (callback) {
      callback(data); // Pass full data including levelUnlocked
    }

    return data; // Return for async/await usage
  } catch (error) {
    console.error("Error submitting test result:", error);

    if (callback) {
      callback(null, error);
    }

    throw error;
  }
};

/**
 * Fetch test history for a user
 * @param {string} userId - User ID
 * @param {object} options - Query options (bookId, limit, offset)
 * @param {function} callback - Callback function to execute after fetch
 */
export const fetchTestHistory =
  (userId, options = {}, callback) =>
  async (dispatch) => {
    try {
      dispatch({ type: LOADING_TEST, payload: true });

      const queryParams = new URLSearchParams(options).toString();
      const { data } = await axios.get(
        `test/results/user/${userId}?${queryParams}`
      );

      dispatch({
        type: FETCH_TEST_HISTORY,
        payload: {
          testResults: data.testResults,
          total: data.total
        }
      });

      dispatch({ type: LOADING_TEST, payload: false });

      if (callback) {
        callback(data);
      }
    } catch (error) {
      console.error("Error fetching test history:", error);
      dispatch({ type: LOADING_TEST, payload: false });

      if (callback) {
        callback({ testResults: [], total: 0 });
      }
    }
  };

/**
 * Fetch user's word level progress
 * @returns {object} Word level progress for all booksets
 */
export const fetchWordLevelProgress = () => async (dispatch) => {
  try {
    const { data } = await axios.get("test/user/word-level-progress");

    dispatch({
      type: FETCH_WORD_LEVEL_PROGRESS,
      payload: data.wordLevelProgress
    });

    return data.wordLevelProgress;
  } catch (error) {
    console.error("Error fetching word level progress:", error);
    return {};
  }
};
